import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://kanguroo-crm-production.up.railway.app/api/v1';
const api = axios.create({ baseURL: API_BASE });
api.interceptors.request.use((c) => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const fmt = (n, cur = 'TRY') => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur }).format(n ?? 0);
const STATUS_LABELS = {
  draft: { label: 'Taslak', color: 'bg-gray-100 text-gray-600' },
  sent: { label: 'Gonderildi', color: 'bg-blue-100 text-blue-700' },
  awaiting: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-700' },
  collected: { label: 'Tahsil Edildi', color: 'bg-green-100 text-green-700' },
  overdue: { label: 'Gecikms', color: 'bg-red-100 text-red-700' },
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/finance/invoices/${id}`)
      .then(async (r) => {
        setInvoice(r.data);
        if (r.data.customer_id) {
          try {
            const c = await api.get(`/customers/${r.data.customer_id}`);
            setCustomer(c.data);
          } catch {}
        }
      })
      .catch(() => alert('Fatura yuklenemedi'))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePrint = () => window.print();

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );

  if (!invoice) return <div className="p-6 text-gray-500">Fatura bulunamadi.</div>;

  const st = STATUS_LABELS[invoice.status] || STATUS_LABELS.draft;
  const items = invoice.items || [];
  const hasMedia = parseFloat(invoice.media_budget_amount) > 0;

  return (
    <>
      {/* Print CSS */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #invoice-print, #invoice-print * { visibility: visible !important; }
          #invoice-print { position: fixed !important; top: 0; left: 0; width: 100%; padding: 32px; background: white; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="p-6 space-y-6 max-w-4xl mx-auto">
        {/* Üst bar */}
        <div className="flex items-center justify-between no-print">
          <button onClick={() => navigate('/finance/invoices')} className="flex items-center gap-2 text-gray-500 hover:text-gray-800 transition text-sm">
            ← Faturalar
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition">
            🖨️ PDF İndir / Yazdir
          </button>
        </div>

        {/* Fatura Kartı */}
        <div id="invoice-print" className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          {/* Header */}
          <div className="bg-slate-800 px-8 py-6 flex items-start justify-between">
            <div>
              <div className="text-white font-bold text-2xl">FATURA</div>
              <div className="text-slate-300 font-mono text-lg mt-1">{invoice.invoice_number}</div>
            </div>
            <div className="text-right">
              <div className="text-white font-bold text-xl">Kanguroo CRM</div>
              <div className="text-slate-400 text-sm mt-1">crm.kanguroo.com.tr</div>
              <span className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold ${st.color}`}>{st.label}</span>
            </div>
          </div>

          <div className="px-8 py-6 space-y-6">
            {/* Musteri & Tarih bilgileri */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-2">Fatura Kesilen</div>
                <div className="font-bold text-gray-900 text-lg">{customer?.company_name || customer?.full_name || '—'}</div>
                {customer?.email && <div className="text-gray-500 text-sm mt-1">{customer.email}</div>}
                {customer?.phone && <div className="text-gray-500 text-sm">{customer.phone}</div>}
              </div>
              <div className="text-right space-y-2">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Fatura Tarihi</div>
                  <div className="text-gray-800 font-medium">{invoice.issued_date ? new Date(invoice.issued_date).toLocaleDateString('tr-TR') : '—'}</div>
                </div>
                {invoice.due_date && (
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Vade Tarihi</div>
                    <div className="text-gray-800 font-medium">{new Date(invoice.due_date).toLocaleDateString('tr-TR')}</div>
                  </div>
                )}
                {invoice.collected_date && (
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Tahsilat Tarihi</div>
                    <div className="text-green-700 font-medium">{new Date(invoice.collected_date).toLocaleDateString('tr-TR')}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Kalemler */}
            {items.length > 0 && (
              <div>
                <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">Kalemler</div>
                <table className="w-full text-sm border border-gray-100 rounded-xl overflow-hidden">
                  <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-2 text-left">Aciklama</th>
                      <th className="px-4 py-2 text-right">Adet</th>
                      <th className="px-4 py-2 text-right">Birim Fiyat</th>
                      <th className="px-4 py-2 text-right">Toplam</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((item, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-gray-700">{item.description}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{item.quantity}</td>
                        <td className="px-4 py-2 text-right text-gray-500">{fmt(item.unit_price, invoice.currency)}</td>
                        <td className="px-4 py-2 text-right font-medium text-gray-800">{fmt(item.total_price, invoice.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Ozet */}
            <div className="border-t border-gray-100 pt-4">
              <div className="flex justify-end">
                <div className="space-y-2 w-64">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Hizmet Tutari</span>
                    <span>{fmt(invoice.service_amount, invoice.currency)}</span>
                  </div>
                  {hasMedia && (
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Medya Butcesi</span>
                      <span>{fmt(invoice.media_budget_amount, invoice.currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold text-gray-900 border-t border-gray-200 pt-2 mt-2">
                    <span>TOPLAM</span>
                    <span className="text-blue-700">{fmt(invoice.total_amount, invoice.currency)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notlar */}
            {invoice.notes && (
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1">Notlar</div>
                <p className="text-gray-600 text-sm">{invoice.notes}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-8 py-4 bg-gray-50 flex justify-between items-center">
            <div className="text-xs text-gray-400">Kanguroo CRM tarafindan olusturulmustur</div>
            <div className="text-xs text-gray-400 font-mono">{invoice.invoice_number}</div>
          </div>
        </div>
      </div>
    </>
  );
}
